import { useCallback, useEffect, useMemo, useState} from "react";
import {PetSummary} from "../types.ts";
import {MultiValue} from "react-select";
import {AgeOption} from "./AgeSelect.tsx";
import {isNullOrEmpty} from "../util.ts";
import {PetFilterControlsProps, PetFilterState} from "./PetFilterControls.tsx";

const DEFAULT_FILTER_STATE: PetFilterState = {
  dogFilter: true,
  catFilter: true,
  critterFilter: true,
  maleFilter: true,
  femaleFilter: true,
  ageFilterValue: [],
  breedFilterValue: [],
  searchTerm: "",
};

type UsePetFilterControlsOptions = {
  petSummaries: PetSummary[];
};

export function usePetFilterControls({
  petSummaries,
}: UsePetFilterControlsOptions) {
  const [filteredPets, setFilteredPets] = useState<PetSummary[]>(petSummaries);

  const [filterState, setFilterState] = useState<PetFilterState>(DEFAULT_FILTER_STATE);

  const setSearchTerm = useCallback((value: string) => {
    setFilterState((prev) => {
      return {
        ...prev,
        searchTerm: value,
      };
    });
  }, [setFilterState]);

  const breedOptions = useMemo(() => {
    const speciesPetSummaries = petSummaries.filter((pet) => {
      if (pet.species.toLowerCase() === "dog" && filterState.dogFilter) return true;
      if (pet.species.toLowerCase() === "cat" && filterState.catFilter) return true;
      if (pet.species.toLowerCase() === "critter" && filterState.critterFilter) return true;
      return false;
    });
    //console.log(speciesPetSummaries);//DEBUG critter is "Rabbit"
    const uniqueBreeds = Array.from(
      new Set(speciesPetSummaries.map((p) => p.breed)),
    ).sort((a, b) => a.localeCompare(b));
    return uniqueBreeds.map((breed) => ({
      value: breed,
      label: breed,
    }));
  }, [petSummaries, filterState.dogFilter, filterState.catFilter, filterState.critterFilter]);


  // Handlers for filter changes
  const handleDogFilterChange = useCallback(() => {
    setFilterState((prev) => {
      return {
        ...prev,
        dogFilter: !prev.dogFilter,
      };
    });
  }, [setFilterState]);

  const handleCatFilterChange = useCallback(() => {
    setFilterState((prev) => {
      return {
        ...prev,
        catFilter: !prev.catFilter,
      };
    });
  }, [setFilterState]);

  const handleCritterFilterChange = useCallback(() => {
    setFilterState((prev) => {
      return {
        ...prev,
        critterFilter: !prev.critterFilter,
      };
    });
  }, [setFilterState]);

  const handleMaleFilterChange = useCallback(() => {
    setFilterState((prev) => {
      return {
        ...prev,
        maleFilter: !prev.maleFilter,
      };
    });
  }, [setFilterState]);

  const handleFemaleFilterChange = useCallback(() => {
    setFilterState((prev) => {
      return {
        ...prev,
        femaleFilter: !prev.femaleFilter,
      };
    });
  }, [setFilterState]);

  const handleAgeFilterChange = useCallback((value: MultiValue<AgeOption>) => {
    setFilterState((prev) => {
      return {
        ...prev,
        ageFilterValue: value,
      };
    });
  }, [setFilterState]);

  const handleBreedFilterChange = useCallback((
    value: MultiValue<{ value: string; label: string }>,
  ) => {
    setFilterState((prev) => {
      return {
        ...prev,
        breedFilterValue: value,
      };
    });
  }, [setFilterState]);

  // effect for URL params, whenever URL changes
  // Parse URL parameters and set state
  useEffect(() => {
    const readFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.has("filters")) {
        const encoded = params.get("filters")!
        const filters = JSON.parse(atob(encoded));
        setFilterState(filters);
      }
    }
    readFromUrl();
    window.addEventListener("popstate", readFromUrl);
    return () => {
      window.removeEventListener("popstate", readFromUrl);
    }
  }, []);

  useEffect(() => {
    const isDefault = JSON.stringify(filterState) === JSON.stringify(DEFAULT_FILTER_STATE);
    if (isDefault) {
      return;
    }
    const params = new URLSearchParams();
    const jsonFilters = JSON.stringify(filterState);
    const encodedFilters = btoa(jsonFilters);
    params.set("filters", encodedFilters);
    window.history.pushState({}, "", `${window.location.pathname}?${params}`);
  }, [filterState]);

  // Effect to update filtered pets whenever a filter changes
  useEffect(() => {
    // Filter pets whenever a filter changes or search term changes
    const filtered = petSummaries.filter((pet) => {
      if (
        !isNullOrEmpty(filterState.searchTerm) &&
        !pet.name.toLowerCase().includes(filterState.searchTerm.toLowerCase())
      ) {
        return false;
      }

      // default true only if no gender filter selected
      let isMatchingGender = !(filterState.maleFilter || filterState.femaleFilter);
      if (filterState.maleFilter && pet.sex.toLocaleLowerCase() === "male") {
        isMatchingGender = true;
      }
      if (filterState.femaleFilter && pet.sex.toLocaleLowerCase() === "female") {
        isMatchingGender = true;
      }
      if (!isMatchingGender) return false;

      // default true only if no species filter selected
      let isMatchingSpecies = !(filterState.dogFilter || filterState.catFilter || filterState.critterFilter);

      if (filterState.dogFilter && pet.species.toLowerCase() === "dog")
        isMatchingSpecies = true;
      if (filterState.catFilter && pet.species.toLowerCase() === "cat")
        isMatchingSpecies = true;
      if (
        filterState.critterFilter &&
        !["dog", "cat"].includes(pet.species.toLocaleLowerCase())
      )
        isMatchingSpecies = true;

      if (!isMatchingSpecies) return false;

      // default true only if no age filters selected
      let inFilteredAgeRange = filterState.ageFilterValue.length === 0;
      for (const filterValue of filterState.ageFilterValue) {
        switch (filterValue.value) {
          case "under-6-months":
            if (pet.ageMonths < 6) {
              inFilteredAgeRange = true;
            }
            break;
          case "6-months-to-1-year":
            if (pet.ageMonths >= 6 && pet.ageMonths < 12) {
              inFilteredAgeRange = true;
            }
            break;
          case "1-to-7-years":
            if (pet.ageMonths >= 12 && pet.ageMonths < 84) {
              inFilteredAgeRange = true;
            }
            break;
          case "over-7-years":
            if (pet.ageMonths >= 84) {
              inFilteredAgeRange = true;
            }
            break;
        }

        if (inFilteredAgeRange) {
          // we don't need to keep checking
          break;
        }
      }

      if (!inFilteredAgeRange) return false;

      // default true only if no age filters selected
      let isMatchingBreed = filterState.breedFilterValue.length === 0;
      for (const filterValue of filterState.breedFilterValue) {
        if (pet.breed === filterValue.value) {
          isMatchingBreed = true;
          break;
        }
      }
      if (!isMatchingBreed) return false;

      return true;
    });
    // Alphabetize by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    setFilteredPets(filtered);
  }, [
    filterState.ageFilterValue,
    filterState.breedFilterValue,
    filterState.dogFilter,
    filterState.catFilter,
    filterState.critterFilter,
    filterState.maleFilter,
    filterState.femaleFilter,
    filterState.searchTerm,
    petSummaries,
  ]);

  const petFilterControlsProps = useMemo<PetFilterControlsProps>(() => {
    return {
      breedOptions,
      filterState,
      handleDogFilterChange,
      handleCatFilterChange,
      handleCritterFilterChange,
      handleMaleFilterChange,
      handleFemaleFilterChange,
      handleAgeFilterChange,
      handleBreedFilterChange,
      setSearchTerm,
    };
  }, [
    breedOptions,
    filterState,
    handleDogFilterChange,
    handleCatFilterChange,
    handleCritterFilterChange,
    handleMaleFilterChange,
    handleFemaleFilterChange,
    handleAgeFilterChange,
    setSearchTerm,
  ]);

  return {
    petFilterControlsProps,
    filteredPets,
  };
}
